/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/indent */
import { Box, useToast } from "@chakra-ui/react"
import { useAtom, useSetAtom } from "jotai"
import React, { useCallback, useEffect, useMemo, useState, type FC } from "react"
import { useNavigate } from "react-router-dom"
import { FMPTableType, Paths } from "../../../common/commonConstants"
import useErrorToast from "../../../common/commonHooks/errorToast"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { Loading } from "../../../components/commons/loader.main"
import { Colors } from "../../../theme/colors"
import {
  useDownloadFmpReportsMutation,
  useExportFmpToExcelMutation,
  useViewUserArchivedFmPsLazyQuery,
  useViewUserFmPsLazyQuery,
  type FmpProject
} from "../../../type-and-hooks"
import { useGetHTMLforPDFDownload } from "../common.fmp.hooks/pdfDownload.hook"
import { pdfGeneratorAtom } from "../common.fmp.hooks/pdfGenerator.store"
import FMPProjectTable from "../fmpProjectTable/fmpProjectTable"
import { MyFMPCompactTable } from "./myFMPs.compactTable"
import { EmptyInitialState } from "./myFMPs.support.EmptyInitialState"
import { type IconKeyValuePairsInterface } from "./myFMPs.support.tableHeaders"

// Column heading background for the My FMPs table - matches the brown
// accent colour used for the "Create New FMP" button.
const MY_FMP_HEADER_BG = Colors.Alternative.accent
// Column heading background for the archived FMPs table (grey instead),
// shared with the empty-state header.
const ARCHIVED_HEADER_BG = "#757575"

interface MyFMPTableProps {
  variant?: FMPTableType.MY_FMP | FMPTableType.MY_ARCHIVED_FMP
}
export const MyFMPTable: FC<MyFMPTableProps> = ({ variant = FMPTableType.MY_FMP }): JSX.Element => {
  const PDFrefToPass = React.useRef<HTMLDivElement>(null)
  const setPdfGenerator = useSetAtom(pdfGeneratorAtom)
  const { generate: generateHTMLForPDF } = useGetHTMLforPDFDownload(PDFrefToPass)
  const [userDetails] = useAtom(userDetailsAtom)
  const [fetchViewUserFMP, { data: userFMPData, loading: userFMPsLoading, error: viewFMPError, refetch: refetchViewUserFMP }] =
    useViewUserFmPsLazyQuery({
      fetchPolicy: "no-cache"
    })
  const [fetchArchivedFMP, { data: archivedFMPData, loading: archivedFMPsLoading, error: viewArchivedFMPError, refetch: refetchViewArchivedFMP }] =
    useViewUserArchivedFmPsLazyQuery({
      fetchPolicy: "no-cache"
    })
  const [exportToExcel, { data: excelDownload, loading: excelDownloadLoading, error: excelDownloadError }] = useExportFmpToExcelMutation()
  const [exportToFMP, { loading: PDFDownloadLoading, data: pdfDownloadedData, error }] = useDownloadFmpReportsMutation()
  const [excelDownloadingId, setExcelDownloadingId] = useState<number | null>(null)
  const [fmpSelectedForPDF, setFmpSelectedForPDF] = useState<number | null>(null)
  const [pdfDownloadOption, setPdfDownloadOption] = useState<number>(1)

  const toast = useToast()
  const userID = userDetails?._id ?? null
  const navigate = useNavigate()

  // Memoized data computation
  const tableData = useMemo(() => {
    if (variant === FMPTableType.MY_FMP) {
      return userFMPData?.viewUserFMPs
    }
    if (variant === FMPTableType.MY_ARCHIVED_FMP) {
      return archivedFMPData?.viewUserArchivedFMPs
    }
    return []
  }, [variant, userFMPData?.viewUserFMPs, archivedFMPData?.viewUserArchivedFMPs])
  const handleShareClick = (fmpId: number, option: number): void => {
    const userId = userDetails?._id

    if (!userId || !fmpId) {
      toast({
        title: "Error",
        description: "Missing user ID or FMP ID",
        status: "error",
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (option !== 2 && !generateHTMLForPDF) {
      toast({
        title: "Error",
        description: "Unable to prepare PDF content. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true
      })
      return
    }

    void exportToFMP({
      variables: {
        downloadFmpReportsId: fmpId,
        userId,
        html: option !== 2 ? generateHTMLForPDF() : "",
        option
      }
    })
  }

  const handlePdfOptionClick = useCallback((id: number | null | undefined, option: number): void => {
    setPdfDownloadOption(option)
    setFmpSelectedForPDF(id ?? null)
  }, [])

  useEffect(() => {
    if (pdfDownloadedData?.downloadFMPReports) {
      handleDownload(pdfDownloadedData.downloadFMPReports, "downloadFMPReports.zip")
    }
  }, [pdfDownloadedData])

  useEffect(() => {
    if (userID && variant === FMPTableType.MY_FMP) {
      fetchViewUserFMP({ variables: { userId: userID } })
    }
    if (userID && variant === FMPTableType.MY_ARCHIVED_FMP) {
      fetchArchivedFMP({ variables: { userId: userID } })
    }
  }, [userDetails, fetchViewUserFMP])

  useEffect(() => {
    if (viewFMPError) {
      toast({
        title: "Error loading Fire Management Plans.",
        description: viewFMPError.message,
        status: "error",
        duration: 5000,
        isClosable: true
      })
    }
  }, [viewFMPError, toast])

  useErrorToast({ toast, error: viewArchivedFMPError, errorMessage: "Something went wrong" })
  useErrorToast({ toast, error: excelDownloadError, errorMessage: "Error exporting FMP to Excel" })
  useErrorToast({ toast, error, errorMessage: "Error generating PDF. Please try again." })

  const handleDownload = useCallback(
    (downloadLink: string, downloadName: string) => {
      if (!downloadLink) {
        toast({
          title: "Error!. Please try again",
          description: "Failed to fetch Download link",
          status: "error",
          duration: 5000,
          isClosable: true
        })
        return
      }
      const link = document.createElement("a")
      link.href = downloadLink
      link.setAttribute("download", downloadName)
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    [toast]
  )

  useEffect(() => {
    if (excelDownload?.exportFMPToExcel) {
      handleDownload(excelDownload.exportFMPToExcel, "downloadExcelReports.zip")
      setExcelDownloadingId(null)
    }
  }, [excelDownload, handleDownload])

  const handleExcelDownload = useCallback(
    (fmpId?: number | null) => {
      try {
        const userId = userDetails?._id
        if (!userId) {
          toast({
            title: "Error",
            description: "User ID is missing. Please log in again.",
            status: "error",
            duration: 5000,
            isClosable: true
          })
          return
        }
        if (!fmpId) {
          toast({
            title: "Error",
            description: "FMP ID is missing. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true
          })
          return
        }
        setExcelDownloadingId(fmpId)
        exportToExcel({
          variables: {
            exportFmpToExcelId: fmpId,
            userId
          }
        }).catch(err => {
          console.error("Excel export error:", err)
          setExcelDownloadingId(null)
        })
      } catch (err) {
        console.error("Excel download click error:", err)
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true
        })
        setExcelDownloadingId(null)
      }
    },
    [exportToExcel, toast, userDetails?._id]
  )

  const handleClick = async (
    item: IconKeyValuePairsInterface["label"],
    id: number | null | undefined,
    projectName?: string | null | undefined
  ): Promise<void> => {
    switch (item) {
      case "View":
        navigate(`${Paths.FMP.VIEW_FMP.replace(":fmpid", id?.toString() ?? "")}`)
        break
      case "Edit":
        navigate(Paths.FMP.EDIT_FMP, { state: { fmpId: id } })
        break
      case "PDF Download":
        // PDF Download functionality
        setFmpSelectedForPDF(id ?? null)
        break
      case "Excel Report":
        handleExcelDownload(id)
        break
      default:
    }
  }

  const getShareUrl = useCallback((fmpId: string | undefined | number) => {
    if (fmpId) {
      return `${location.href}/${fmpId.toString()}`
    }
  }, [])

  const refetchData = useCallback(() => {
    if (variant === FMPTableType.MY_FMP) {
      refetchViewUserFMP()
    }
    if (variant === FMPTableType.MY_ARCHIVED_FMP) {
      refetchViewArchivedFMP()
    }
  }, [])
  useEffect(() => {
    // const generatedPDFHTML = generateHTMLForPDF()
    // triggers a function that generates HTML for PDF when fmpSelectedForPDF changes, and sends that HTML for backend PDF generation.
    if (fmpSelectedForPDF !== null) {
      handleShareClick(fmpSelectedForPDF, pdfDownloadOption)
    }
  }, [fmpSelectedForPDF])

  return (
    <Box w="100%" overflow="auto">
      <Loading loading={PDFDownloadLoading} />
      <Box mt="0" pos="relative" minW="800px" minH="300px">
        {(userFMPsLoading || archivedFMPsLoading) && (
          <Box left="0" top="0" w="100%" height="100%" pos="absolute" display="flex" alignItems="center" justifyContent="center" mt="8">
            <Loading loading={userFMPsLoading || archivedFMPsLoading} />
          </Box>
        )}
        <Box display="flex" gridGap="4px" w="100%" justifyContent="space-between"></Box>
        {!tableData?.length && !archivedFMPsLoading && !userFMPsLoading && (
          <EmptyInitialState
            message={variant === FMPTableType.MY_ARCHIVED_FMP ? "No FMPs archived yet" : "No FMPs created yet"}
            headerBg={variant === FMPTableType.MY_ARCHIVED_FMP ? ARCHIVED_HEADER_BG : MY_FMP_HEADER_BG}
          />
        )}
        {Boolean(tableData?.length) && (
          <MyFMPCompactTable
            tableData={tableData}
            handleClick={(item, id, projectName) => { void handleClick(item, id, projectName) }}
            onPdfOptionClick={handlePdfOptionClick}
            refetchData={refetchData}
            getShareUrl={getShareUrl}
            PDFDownloadLoading={PDFDownloadLoading}
            fmpSelectedForPDF={fmpSelectedForPDF}
            excelDownloadingId={excelDownloadingId}
            excelDownloadLoading={excelDownloadLoading}
            headerBg={variant === FMPTableType.MY_ARCHIVED_FMP ? ARCHIVED_HEADER_BG : MY_FMP_HEADER_BG}
            isArchived={variant === FMPTableType.MY_ARCHIVED_FMP}
          />
        )}
        {tableData?.map((fmpRow: FmpProject | null) => (
          <React.Fragment key={`PDFTableRow${fmpRow?.id}`}>
            {fmpRow?.id === fmpSelectedForPDF && (
              // style={{ position: "absolute", left: "-9999px", top: 0, opacity: 0 }}
              <Box ref={PDFrefToPass} hidden={true}>
                <FMPProjectTable fmpId={Number(fmpRow?.id)} tableType={FMPTableType.VIEW_FMP} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
}
